import type React from "react";
import { AccordionDetails, AccordionSummary, Typography } from "~/common/mui";
import type { DetailedOrderData } from "~/domains/orders/types/modelsV2";
import { Param } from "~/features/orders/common/components/v2/Params";
import { useTranslation } from "react-i18next";
import { DateFormatType, formatNullableDate } from "~/common/utils/DateFormatter";
import Address from "~/features/cart/checkout/components/Address";
import OrderTotals from "~/features/cart/cart/components/OrderTotals";
import { useMemo } from "react";
import { getOrderLineItemSurchargeValue } from "~/features/orders/common/utils/OrderLineItemUtils";
import { sum } from "~/utils/PriceUtils";
import Tag from "~/common/components/Tag/Tag";
import {
  AccordionDetailsVisibleData,
  DataStack,
  DetailsRow,
  MyStyledAccordion,
  ODetailsSummary,
  OverView,
  VisibleData,
} from "../../common/components/v2/OrderAndQuoteSharedStyles";

interface OrderDetailsSummaryProps {
  data: DetailedOrderData;
}

const OrderDetailsSummaryV2: React.FC<OrderDetailsSummaryProps> = ({ data }) => {
  const { t } = useTranslation();
  const totalSurcharge = useMemo(() => {
    return sum(...data.products.map((lineItem) => getOrderLineItemSurchargeValue(lineItem)));
  }, [data]);
  return (
    <ODetailsSummary>
      <DataStack>
        <VisibleData>
          <DetailsRow>
            <Param label={t("orderListPage.table.header.poNumber")} value={data.poNumber} />
            <Param label={t("orderListPage.table.header.soNumber")} value={data.soNumber} />

            <Param
              label={t("orderDetailsPage.summary.status")}
              value={
                <Tag variant={data.orderStatus === "codes.orderStatus.H6" ? "success" : "default"}>
                  {t(data.orderStatus)}
                </Tag>
              }
            />
            <Param label={t("orderDetailsPage.summary.orderType")} value={t(data.orderType)} />
            <Param
              label={t("contractDetailsPage.summary.datePlaced")}
              value={formatNullableDate(data.datePlaced, DateFormatType.DATE)}
            />
          </DetailsRow>
        </VisibleData>
        <MyStyledAccordion>
          <AccordionSummary>
            <Typography variant="bodyLarge">{t("orderDetails.shipping.and.billing.text")}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <AccordionDetailsVisibleData>
              <DetailsRow>
                <Param label={t("orderDetailsPage.summary.accNo")} value={data.accNo} />
                <Param label={t("orderDetailsPage.summary.shipmentMethod")} value={t(data.shipmentMethod)} />
                <Param label={t("contractDetailsPage.summary.freight")} value={t(data.freightPayment)} />
                <Param
                  label={t("orderDetails.billing.address.text")}
                  value={
                    <Typography variant="bodyLarge" color="textSecondary">
                      <Address as={"span"} data={data.soldTo} />
                    </Typography>
                  }
                />
                <Param
                  label={t("checkoutPage.headers.shippingAddress")}
                  value={
                    <Typography variant="bodyLarge" color="textSecondary">
                      <Address as={"span"} data={data.shippingAddress} />
                    </Typography>
                  }
                />
              </DetailsRow>
            </AccordionDetailsVisibleData>
          </AccordionDetails>
        </MyStyledAccordion>
      </DataStack>
      <OverView>
        <OrderTotals
          showPriceOnRequest={false}
          weight={data.totalWeight}
          totalValue={data.totalPrice}
          surcharge={totalSurcharge.value > 0 ? totalSurcharge : undefined}
        />
      </OverView>
    </ODetailsSummary>
  );
};

export default OrderDetailsSummaryV2;
